import { useMemo, useState } from 'react';
import { Box, HStack, Popover, Pressable, VStack } from 'native-base';
import { range } from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Text, CalendarIcon, InfoCircleIcon } from '@pimm/base';
import { CleaningScheduleTypeEnum } from '@pimm/services/lib/sms-workforce/types';
import { CalendarPicker, CalendarPickerProps } from '@app/components/shared';

const DAYS_ABBR = range(7).map(i => moment().day(i).format('ddd'));

type DayInfo = { year: number; month: number; date: number; day: number };

export type PopoverDateTimeProps = {
  scheduleType: CleaningScheduleTypeEnum;
  /** Current full date+time — date portion drives the selection display. */
  value: Date;
  /** Daily only: the task's specific calendar day (chip 0). */
  taskDate?: Date;
  /** Daily only: DOP start hour (e.g. 5 → "5:00 AM"). Derived from blockStart.getHours(). */
  dopStartHour?: number;
  /** Daily only: which chip is active — 0 = task day, 1 = next day. */
  dailyDayIdx?: number;
  /** Called when the selected date changes (time portion preserved from value). */
  onDateChange: (date: Date) => void;
  /** Called when Daily day chip index changes. */
  onDailyDayIdxChange?: (idx: number) => void;
} & Pick<CalendarPickerProps, 'max' | 'min'> &
  Omit<React.ComponentProps<typeof Popover>, 'children' | 'trigger'>;

export const PopoverDateTime = ({
  scheduleType,
  value,
  taskDate,
  dopStartHour = 5,
  dailyDayIdx = 0,
  min,
  max,
  onDateChange,
  onDailyDayIdxChange,
}: PopoverDateTimeProps) => {
  const { t } = useTranslation();
  const [showCal, setShowCal] = useState(false);

  const sel = moment(value);
  const selYear = sel.year();
  const selMonth = sel.month();
  const selDay = sel.date();

  /** Preserve the time portion from `value` when changing the date. */
  const selectDate = (y: number, m: number, d: number) => {
    onDateChange(sel.clone().year(y).month(m).date(d).toDate());
  };

  // ── Weekly: 7 chips from min→max ──────────────────────────────────────────
  const weekDays = useMemo<DayInfo[]>(() => {
    if (scheduleType !== CleaningScheduleTypeEnum.Weekly || !min || !max) return [];
    const totalDays = moment(max).diff(moment(min), 'days') + 1;
    return range(totalDays).map(i => {
      const d = moment(min).add(i, 'days');
      return { year: d.year(), month: d.month(), date: d.date(), day: d.day() };
    });
  }, [scheduleType, min, max]);

  // ── Daily: task day + next day ────────────────────────────────────────────
  const dailyDays = useMemo<DayInfo[]>(() => {
    if (scheduleType !== CleaningScheduleTypeEnum.Daily || !taskDate) return [];
    return range(2).map(index => {
      const d = moment(taskDate).add(index, 'days');
      return { year: d.year(), month: d.month(), date: d.date(), day: d.day() };
    });
  }, [scheduleType, taskDate]);

  // ── Weekly ────────────────────────────────────────────────────────────────
  if (scheduleType === CleaningScheduleTypeEnum.Weekly) {
    return (
      <HStack flexWrap="wrap" alignItems="flex-start">
        {weekDays.map((wd, i) => {
          const isSelected = wd.year === selYear && wd.month === selMonth && wd.date === selDay;

          return (
            <Pressable
              key={i}
              w="44px"
              h="44px"
              mr={1.5}
              mb={1.5}
              alignItems="center"
              justifyContent="center"
              borderRadius="xl"
              borderWidth={1}
              borderColor="gray.200"
              bg={isSelected ? 'yellow.400' : 'white'}
              onPress={() => selectDate(wd.year, wd.month, wd.date)}
            >
              <Text size="sm" fontWeight={500} color={isSelected ? 'gray.900' : 'gray.500'} lineHeight="xs">
                {DAYS_ABBR[wd.day]}
              </Text>
              <Text size="lg" fontWeight={700} color={isSelected ? 'gray.900' : 'gray.700'} lineHeight="xs">
                {wd.date}
              </Text>
            </Pressable>
          );
        })}
      </HStack>
    );
  }

  // ── Monthly: CalendarPicker in a Popover ─────────────────────────────────
  if (scheduleType === CleaningScheduleTypeEnum.Monthly) {
    return (
      <Box mb={3}>
        <Popover
          isOpen={showCal}
          onClose={() => setShowCal(false)}
          placement="bottom"
          useRNModal
          trigger={triggerProps => (
            <Pressable
              {...triggerProps}
              onPress={() => setShowCal(_date => !_date)}
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              py={2}
              borderWidth={1}
              borderColor="gray.200"
              borderRadius="md"
            >
              <CalendarIcon size={3} color="gray.700" mr={2} />
              <Text size="md" fontWeight={500} color="gray.900">
                {moment(value).format('ddd MMM D, YYYY')}
              </Text>
            </Pressable>
          )}
        >
          <Popover.Content borderRadius="lg" shadow={4} p={0}>
            <Popover.Arrow />
            <Popover.Body p={0}>
              <CalendarPicker
                date={value}
                min={min}
                max={max}
                onChange={date => {
                  selectDate(date.getFullYear(), date.getMonth(), date.getDate());
                  setShowCal(false);
                }}
              />
            </Popover.Body>
          </Popover.Content>
        </Popover>
      </Box>
    );
  }

  // ── Daily: 2 day chips ────────────────────────────────────────────────────
  return (
    <VStack space={2.5}>
      <HStack space={1.5}>
        {dailyDays.map((dd, idx) => {
          const isSelected = dailyDayIdx === idx;
          return (
            <Pressable
              key={idx}
              flex={1}
              h="44px"
              alignItems="center"
              justifyContent="center"
              borderRadius="xl"
              borderWidth={1}
              borderColor="gray.200"
              bg={isSelected ? 'yellow.400' : 'white'}
              onPress={() => onDailyDayIdxChange?.(idx)}
            >
              <Text size="sm" fontWeight={500} color={isSelected ? 'gray.900' : 'gray.500'} lineHeight="xs">
                {DAYS_ABBR[dd.day]}
              </Text>
              <Text size="lg" fontWeight={700} color={isSelected ? 'gray.900' : 'gray.700'} lineHeight="xs">
                {dd.date}
              </Text>
            </Pressable>
          );
        })}
      </HStack>

      {/* Time constraint hint */}
      <HStack alignItems="center" space={1.5} px={1}>
        <InfoCircleIcon size={3} color={dailyDayIdx === 0 ? 'gray.600' : 'primary.600'} />
        <Text size="sm" fontWeight={500} color={dailyDayIdx === 0 ? 'gray.600' : 'primary.700'} lineHeight="sm">
          {dailyDayIdx === 0
            ? `${t('cleaning_task:store_hours')}: ${moment({ hour: dopStartHour, minute: 0 }).format('h:mm A')} – ${moment({
                hour: 23,
                minute: 55,
              }).format('h:mm A')}`
            : `${t('cleaning_task:overnight')}: ${moment({ hour: 0, minute: 0 }).format('h:mm A')} – ${moment({
                hour: dopStartHour - 1,
                minute: 55,
              }).format('h:mm A')}`}
        </Text>
      </HStack>
    </VStack>
  );
};

export default PopoverDateTime;
